import {Observable, of} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {VoidDto} from "../dto/void.dto";
import {HttpClient} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {UrlBuilder} from "../item/url.item";
import {isDefined, isUuid, nonBlank, notDefined} from "../../libs/utils/epic.utils";
import {UrlDto} from "../item/url.dto";
import {DUCKDB_TRANSPORT, DuckDbTransport} from "./duckdb-transport";
import {SearchRequest} from "../dto/search/search-table.dto.req";

/**
 * Maskiert einen Wert als SQL-Zeichenkette. Ohne sie zerlegt ein einzelnes Apostroph die Abfrage —
 * „O'Brien" oder „L'Atelier" im Suchfeld reichen dafür aus.
 *
 * ⚠️ **Ohne Wert entsteht der blanke Token `NULL`**, und `spalte = NULL` trifft in SQL auf nichts zu —
 * auch nicht auf NULL-Werte. Ein Aufruf mit einem möglicherweise leeren `selected[0]` liefert deshalb
 * stillschweigend eine leere Liste statt einer Fehlermeldung. Wer filtert, muss den leeren Fall vorher
 * selbst entscheiden: bei einem Facettenfilter heißt leer „nicht filtern", bei einem Kontextfilter
 * (Kontakt-, Fahrzeug-, Belegzugehörigkeit) heißt leer `1=0` — „alles" wäre dort der fremde Datensatz.
 */
export function sqlText(value: string | number | undefined | null): string {
  if (notDefined(value)) {
    return 'NULL';
  }
  return `'${String(value).replace(/'/g, "''")}'`;
}

/**
 * Vollständiges LIKE-Muster einschließlich der umschließenden Platzhalter — `sqlLike('a')` ergibt `'%a%'`.
 *
 * Maskiert wird ausschließlich das Apostroph, nicht `%` und `_`. Das ist eine bewusste Entscheidung:
 * Eine Maskierung dieser beiden wirkt nur zusammen mit einer `ESCAPE`-Klausel an jeder einzelnen
 * Fundstelle; ohne sie würde stattdessen nach dem Gegenschrägstrich gesucht. Tippt jemand ein `%`,
 * sucht er breiter als gedacht — ein Schönheitsfehler. Ein Apostroph dagegen zerlegt die Abfrage, und
 * das ist der Fall, gegen den diese Funktion steht.
 */
export function sqlLike(value: string | undefined | null): string {
  if (notDefined(value)) {
    return `'%%'`;
  }
  return `'%${String(value).replace(/'/g, "''")}%'`;
}

/**
 * Sortierrichtung. An dieser Stelle hilft kein Maskieren — das Feld steht hinter `ORDER BY` und ist
 * dort kein Wert. Deshalb wird nur eine der beiden erlaubten Richtungen durchgelassen.
 *
 * ⚠️ **Der Rückfall ist `ASC`.** `SearchRequest.sortOrder` ist optional — ein Rückfall auf `DESC` würde
 * jede Anfrage ohne Richtungsangabe verkehrt herum sortieren.
 */
export function sqlDirection(direction: string | undefined | null): string {
  return String(direction ?? '').toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
}

/**
 * Nichtnegative Ganzzahl — für `LIMIT` und `OFFSET`, wo kein Maskieren greift.
 *
 * ⚠️ **Bewusst ohne Obergrenze.** Ein Deckel schützt hier nichts: Der Offset wird aus `pageSize` und
 * `page` gerechnet, kommt also nicht aus einer Eingabe. Er würde nur dafür sorgen, dass ab einer
 * bestimmten Seite stillschweigend immer dieselben Zeilen kämen, während der Seitenzähler weiterzählt —
 * aus einer tiefen Seite würden falsche Daten statt eines Fehlers.
 *
 * ⚠️ **`Number()` allein reicht als Prüfung nicht.** Es macht aus `null`, `''`, `'   '`, `[]` und
 * `false` eine glatte `0` — endlich und nicht negativ, der Rückfall griffe also nicht. Für ein `LIMIT`
 * ist das eine leere Liste ohne Fehlermeldung; nur `undefined` und `NaN` kämen je beim Rückfall an.
 * Deshalb werden ausschließlich Zahlen und nichtleere Zeichenketten überhaupt umgewandelt.
 */
export function sqlNumber(value: unknown, fallback: number): number {
  const usable: boolean = typeof value === 'number' || (typeof value === 'string' && nonBlank(value));
  if (!usable) {
    return fallback;
  }
  const parsed: number = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.floor(parsed);
}

/**
 * Kontextfilter der Reiter am Fahrzeug (Verlauf, Reparaturen, Reifen), wie im HTTP-Repository an Position 1001. Ohne
 * Fahrzeug heisst er `1=0`: "alles" waeren dort die Eintraege fremder Fahrzeuge.
 */
export function sqlVehicleFilter(request: SearchRequest, column: string): string {
  const vehicleId: string | undefined = request.filters.find((f) => f.pos === 1001)?.selected?.find((s) => isUuid(s));
  return isDefined(vehicleId) ? `${column} = ${sqlText(vehicleId)}` : '1=0';
}

/**
 * Ja/Nein-Filter aus der Route (`filterReviewed=false`) oder der Auswahl. Entscheidend ist der Wert, nicht dass der Filter gesetzt
 * ist: "ungeprueft" und "geprueft" teilen sich eine Filterkennung. Beide Werte zugleich schraenken nichts ein.
 */
export function sqlYesNo(request: SearchRequest, filterId: string, yes: string, no: string): string | undefined {
  const selected: string[] = (request.filters.find((f) => f.id === filterId)?.selected ?? []).map((s) => String(s).toLowerCase());
  const wantsYes: boolean = selected.includes('true');
  const wantsNo: boolean = selected.includes('false');
  return wantsYes === wantsNo ? undefined : wantsYes ? yes : no;
}

/** Facettenfilter: ohne Auswahl keine Einschraenkung, sonst eine der gewaehlten Auspraegungen. */
export function sqlSelectedIn(request: SearchRequest, filterId: string, column: string): string | undefined {
  const selected: string[] = request.filters.find((f) => f.id === filterId)?.selected ?? [];
  return selected.length > 0 ? `${column} IN (${selected.map((s) => sqlText(s)).join(', ')})` : undefined;
}

/** Die Teile einer Anschrift, wie die Abfragen sie liefern. */
export interface LetterAddressParts {
  fullName?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  addressSuffix?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryAlpha2?: string | null;
}

/** Anschrift in Briefform, Zeilen mit <br> getrennt - so liefert core sie fuer Rechnung und Kontakt. */
export function buildLetterAddress(res: LetterAddressParts): string {
  const address: string[] = [];
  const name: string = nonBlank(res.fullName) ? String(res.fullName) : '';
  const street: string = nonBlank(res.street) ? String(res.street) : '';
  const houseNumber: string = nonBlank(res.houseNumber) ? String(res.houseNumber) : '';
  const streetWithNo: string = street + ' ' + houseNumber + (isDefined(res.addressSuffix) ? ' // ' + res.addressSuffix : '');
  const zipCode: string = nonBlank(res.zipCode) ? String(res.zipCode) : '';
  const city: string = nonBlank(res.city) ? String(res.city) : '';
  const location: string = zipCode + ' ' + city + (isDefined(res.countryAlpha2) ? ' // ' + res.countryAlpha2 : '');

  if (nonBlank(name)) {
    address.push(name);
  }
  if (nonBlank(streetWithNo)) {
    address.push(streetWithNo);
  }
  if (nonBlank(location)) {
    address.push(location);
  }
  return address.join('<br>');
}

@Injectable({providedIn: 'root'})
export class DuckDbHelper {

  private readonly http: HttpClient = inject(HttpClient);
  private readonly transport: DuckDbTransport | null = inject(DUCKDB_TRANSPORT, {optional: true});

  public sql(sql: string): Observable<any> {
    if (isDefined(this.transport)) {
      return this.transport.query(sql);
    }
    const query: string = JSON.stringify({sql: sql});
    const url: UrlDto = UrlBuilder.builder().build();
    // Das Sitzungstoken setzt viewerTokenInterceptor, nicht diese Methode: Der Webserver des Viewers
    // prüft es auch am Medienabruf, und der läuft hier nicht durch.
    return this.http.post(url.getUrl(), query, url.getOptions());
  }

  public static data<T>(type: string, data: T): WrapperDto<T> {
    return this.datas(type, [data]);
  }

  public static datas<T>(type: string, datas: T[]): WrapperDto<T> {
    return new WrapperDto<T>({
      ts: new Date(),
      dataType: type,
      datas: datas,
      errors: [],
      status: 200,
      code: 'OK'
    });
  }

  public static dataObs<T>(type: string, data: T): Observable<WrapperDto<T>> {
    return of(this.data<T>(type, data));
  }

  public static datasObs<T>(type: string, datas: T[]): Observable<WrapperDto<T>> {
    return of(this.datas<T>(type, datas));
  }

  public static voidObs(): Observable<WrapperDto<VoidDto>> {
    return this.dataObs('VoidDto', new VoidDto());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static hasRes(res: any): boolean {
    return isDefined(res) && res.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static firstRes(res: any): any {
    if (DuckDbHelper.hasRes(res)) {
      return res[0];
    }
    return undefined;
  }
}
